import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import UserService from '../../services/userService';
import ResumeAnalysisService from '../../services/resumeAnalysisService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import WelcomeCard from './components/WelcomeCard';
import AccountStatusCard from './components/AccountStatusCard';
import ProgressChart from './components/ProgressChart';
import { supabase } from '../../lib/supabase';
import RecentAnalysisCard from './components/RecentAnalysisCard';
import QuickActionsGrid from './components/QuickActionsGrid';
import SkillProgressCard from './components/SkillProgressCard';
import Footer from '../../components/ui/Footer';

const getJobMatch = (analysis) => {
  if (typeof analysis?.job_match_score === 'number') return analysis.job_match_score;
  const fromJson =
    analysis?.analysis_results?.analysis?.job_match_score ??
    analysis?.analysis_results?.job_match_score;
  return typeof fromJson === 'number' ? fromJson : 0;
};

const UserDashboard = () => {
  const { user, userProfile, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user && userProfile) {
      loadDashboardData();
    }
  }, [user, userProfile]);

  const loadDashboardData = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const { data: profile, error: profileError } = await UserService.getProfileWithStats(user.id);

      if (profileError) {
        console.error('Failed to load profile data:', profileError);
        setProfileData({
          ...userProfile,
          stats: {
            total_analyses: 0,
            skill_gaps_count: 0,
            average_score: 0,
            progress_data: []
          },
          skills: []
        });
      } else {
        setProfileData(profile);
      }

      const analyses = await ResumeAnalysisService.getUserAnalyses(user.id, 10);
      setRecentAnalyses(analyses || []);

      const { data: payments } = await UserService.getPaymentIterations(user.id);
      setPaymentHistory(payments || []);

      // CRITICAL: Exclude FREE tier from skill roadmaps (they have no recommendations)
      const paidAnalysesForRoadmap = (analyses || []).filter(a =>
        a?.analysis_results?.paymentStatus?.tier !== 'free'
      );

      // Calculate scan numbers first
      const jobCounts = {};
      const analysesWithScanNumbers = paidAnalysesForRoadmap.map(a => {
        const posting = a?.analysis_results?.jobPosting || {};
        const title = typeof posting?.title === 'string' ? posting.title : a?.file_name || 'Live Analysis';
        const jobKey = a.target_role || title || 'unknown';
        jobCounts[jobKey] = (jobCounts[jobKey] || 0) + 1;
        return { ...a, scanNumber: jobCounts[jobKey] };
      });

      const groupedNext = analysesWithScanNumbers
        .map((a) => {
          const live = a?.analysis_results?.analysis;
          const posting = a?.analysis_results?.jobPosting || {};
          const title = typeof posting?.title === 'string' ? posting.title : a?.file_name || 'Live Analysis';
          const roadmap = Array.isArray(live?.skill_roadmap) ? live.skill_roadmap : [];

          const items = [];
          roadmap.forEach((item) => {
            if (item?.premium_course?.url) {
              items.push({
                skill: item.skill || 'Skill',
                type: 'Course',
                title: item.premium_course.title,
                provider: item.premium_course.provider,
                url: item.premium_course.url
              });
            }
            if (item?.youtube_top?.url) {
              items.push({
                skill: item.skill || 'Skill',
                type: 'Video',
                title: item.youtube_top.title,
                provider: item.youtube_top.channel,
                url: item.youtube_top.url
              });
            }
            if (!item?.premium_course?.url && !item?.youtube_top?.url) {
              items.push({
                skill: item.skill || 'Skill',
                type: 'Action',
                title: item.why_it_matters || 'Focus on this skill'
              });
            }
          });

          return {
            id: a.id,
            created_at: a.created_at,
            title,
            items: items.slice(0, 3),
            scanNumber: a.scanNumber
          };
        })
        .filter((g) => (g.items || []).length > 0)
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

      // CRITICAL: Exclude FREE tier scans from metrics - they're previews, not real data
      const paidAnalyses = (analyses || []).filter(a =>
        a?.analysis_results?.paymentStatus?.tier !== 'free'
      );

      const chartData = paidAnalyses
        .map((a, idx, arr) => ({
          name: `#${arr.length - idx}`,
          score: getJobMatch(a)
        }))
        .reverse();

      const jmSeries = paidAnalyses.map((a) => getJobMatch(a));
      const bestJM = jmSeries.length ? Math.max(...jmSeries) : 0;
      const latestJM = jmSeries.length ? jmSeries[0] : 0;

      setProfileData((prev) => {
        const prevStats = (prev && prev.stats) || {};
        return {
          ...(prev || {}),
          stats: {
            ...prevStats,
            grouped_next_actions: groupedNext,
            job_match_series: chartData,
            job_match: {
              series: jmSeries,
              best: bestJM,
              latest: latestJM
            }
          }
        };
      });
    } catch (err) {
      console.error('Dashboard load error:', err);
      setProfileData({
        ...userProfile,
        stats: {
          total_analyses: 0,
          skill_gaps_count: 0,
          average_score: 0,
          progress_data: []
        },
        skills: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (err) {
      console.error('Sign out error:', err);
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  const userName = userProfile?.full_name || user?.email?.split('@')?.[0] || 'User';
  const userTier = userProfile?.tier || 'free';
  const creditsRemaining = userProfile?.credits_remaining ?? 1;

  const trackedSkillsCount =
    profileData?.stats?.skill_gaps_count ??
    (() => {
      const sections = profileData?.stats?.grouped_next_actions || [];
      const uniqueSkills = new Set();
      sections.forEach((section) => {
        section?.items?.forEach((item) => {
          if (item?.skill) {
            uniqueSkills.add(item.skill);
          }
        });
      });
      return uniqueSkills.size;
    })();

  const defaultProgressData = [
    { name: 'Analysis #1', score: 50 },
    { name: 'Your Target', score: 85 }
  ];

  const defaultSkills = [
    { name: 'React', icon: 'Code', progress: 30, category: 'Frontend', completedTasks: 1, totalTasks: 5, isImproving: false },
    { name: 'Python', icon: 'Terminal', progress: 25, category: 'Backend', completedTasks: 1, totalTasks: 5, isImproving: false },
    { name: 'SQL', icon: 'Database', progress: 20, category: 'Data', completedTasks: 0, totalTasks: 3, isImproving: false }
  ];

  const validateChartData = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return defaultProgressData;
    }
    const validData = data.filter(
      (item) =>
        item &&
        typeof item.score === 'number' &&
        !isNaN(item.score) &&
        isFinite(item.score) &&
        item.score >= 0 &&
        item.score <= 100
    );
    return validData.length > 0 ? validData : defaultProgressData;
  };

  const progressData = validateChartData(profileData?.stats?.progress_data || []);

  const skillsData =
    profileData?.skills && Array.isArray(profileData.skills) && profileData.skills.length > 0
      ? profileData.skills
      : defaultSkills;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Zap" size={20} color="white" />
              </div>
              <span className="text-xl font-semibold text-foreground">JobAspirant</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative group">
                <button className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted transition-colors">
                  <div className="w-8 h-8 bg-primary/10 text-primary rounded-full flex items-center justify-center text-sm font-medium">
                    {userName?.charAt(0)?.toUpperCase()}
                  </div>
                  <span className="text-sm font-medium text-foreground">{userName}</span>
                  <Icon name="ChevronDown" size={14} className="text-muted-foreground" />
                </button>
                <div className="absolute right-0 top-full mt-1 w-48 bg-card border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="py-1">
                    <div className="px-3 py-2 text-xs text-muted-foreground border-b border-border">
                      <div className="font-medium text-foreground">{userName}</div>
                      <div className="capitalize">{userTier} Plan</div>
                    </div>
                    <button
                      onClick={() => navigate('/upload-analysis')}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                    >
                      New Analysis
                    </button>
                    <div className="border-t border-border">
                      <button
                        onClick={handleSignOut}
                        className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-muted transition-colors"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* No Credits Warning Banner */}
      {creditsRemaining === 0 && (
        <div className="bg-warning/10 border-b-2 border-warning">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="text-2xl">⚠️</span>
                <div>
                  <p className="font-semibold text-warning">No Credits Remaining</p>
                  <p className="text-sm text-muted-foreground">
                    You need credits to analyze resumes. Get 2 credits now!
                  </p>
                </div>
              </div>
              <Button
                variant="default"
                onClick={() => navigate('/upgrade')}
                iconName="CreditCard"
                iconPosition="left"
              >
                💳 Buy 2 Credits
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            <WelcomeCard
              userName={userName}
              tier={userTier}
              creditsRemaining={creditsRemaining}
              user={user}
              onNewAnalysis={() => navigate('/upload-analysis')}
            />

            <ProgressChart data={profileData?.stats?.job_match_series || []} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <RecentAnalysisCard
                analyses={recentAnalyses}
                onViewDetails={async (id) => {
                  // Fetch full analysis data from database
                  const { data, error } = await supabase
                    .from('resume_analyses')
                    .select('*')
                    .eq('id', id)
                    .single();

                  if (error || !data) {
                    console.error('Failed to load analysis:', error);
                    return;
                  }

                  // Navigate to results page with full data AND paymentStatus
                  navigate('/analysis-results-live', {
                    state: {
                      analysisId: id,
                      analysis: data.analysis_results?.analysis,
                      jobPosting: data.analysis_results?.jobPosting,
                      paymentStatus: data.analysis_results?.paymentStatus,
                      enhanced_comparison: data.enhanced_comparison // Include comparison data
                    }
                  });
                }}
              />
              <SkillProgressCard groupedNextActions={profileData?.stats?.grouped_next_actions || []} />
            </div>
          </div>

          <div className="space-y-6">
            <AccountStatusCard
              tier={userTier}
              creditsRemaining={creditsRemaining}
              totalAnalyses={profileData?.stats?.total_analyses ?? 0}
              bestMatchScore={profileData?.stats?.job_match?.best ?? 0}
              trackedSkills={trackedSkillsCount}
              totalCreditsUsed={profileData?.stats?.total_analyses ?? 0}
              onUpgrade={() => navigate('/upgrade')}
            />

            <div className="bg-card border border-border rounded-lg p-4 space-y-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Payment history</p>
              {paymentHistory?.length ? (
                <>
                  <ul className="space-y-2 text-sm">
                    {paymentHistory.slice(0, 5).map((payment) => (
                      <li key={payment.order_id} className="border border-border/60 rounded-md px-3 py-2">
                        <p className="font-medium">
                          Payment #{payment.payment_iteration} • {payment.order_id}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(payment.created_at).toLocaleString()} • +{payment.credits_awarded} credits
                        </p>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-muted-foreground">
                    Keep these order IDs handy when contacting support about payments.
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No payments yet. Use your free scan to get started.
                </p>
              )}

            </div>

            <QuickActionsGrid
              onAction={(action) => {
                if (action === 'ai-chat') {
                  alert('AI Career Chat is launching soon for paid users! Stay tuned.');
                  return;
                }
                navigate(`/${action}`);
              }}
            />

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-card-foreground">Recommended Next Actions</h3>
              </div>

              {!profileData?.stats?.next_actions?.length ? (
                <p className="text-sm text-muted-foreground">
                  Run a new analysis to get personalized actions.
                </p>
              ) : (
                <div className="space-y-3">
                  {profileData.stats.next_actions.slice(0, 3).map((a, idx) => (
                    <div
                      key={`${a.url || a.title || idx}-${idx}`}
                      className="flex items-start justify-between gap-3 bg-muted/30 rounded-md px-3 py-2"
                    >
                      <div>
                        <div className="text-xs text-muted-foreground mb-0.5">
                          {a.skill} · {a.type}
                        </div>
                        <div className="text-sm font-medium text-card-foreground line-clamp-2">
                          {a.title || 'Recommended resource'}
                        </div>
                        {a.provider && (
                          <div className="text-xs text-muted-foreground mt-0.5">{a.provider}</div>
                        )}
                      </div>
                      {a.url ? (
                        <a
                          href={a.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-2 py-1 rounded-md border border-border hover:bg-muted transition-colors"
                        >
                          Open
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground px-2 py-1">No link</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UserDashboard;